// @ts-check
import { Edit2, MoreHorizontal, Plus, Save, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import FashionModelsTab from '../components/FashionModelsTab';
import LoadingScreen from '../components/LoadingScreen';
import { useToast } from '../context/ToastContext';
import { useHasAnyUnsavedChanges, useRegisterUnsavedChanges } from '../context/UnsavedChangesContext';
import api from '../utils/apiClient';

const CatalogSettingsPage = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('sizes');
  // Only one tab's form is ever mounted at a time on this page, so the
  // app-wide "is anything dirty" aggregate is exactly "does the CURRENT
  // tab's form have unsaved changes" here — switching tabs unmounts the
  // active tab's component, which would otherwise silently discard it.
  const hasUnsavedChanges = useHasAnyUnsavedChanges();
  const changeTab = (nextTab) => {
    if (nextTab === activeTab) return;
    if (hasUnsavedChanges && !window.confirm('You have unsaved changes. Switch tabs anyway?')) return;
    setActiveTab(nextTab);
  };
  const [sizes, setSizes] = useState([]);
  const [compositions, setCompositions] = useState([]);
  const [badges, setBadges] = useState([]);
  const [careInstructions, setCareInstructions] = useState([]);
  const [fashionModels, setFashionModels] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sizesData, compositionsData, badgesData, careData, modelsData] = await Promise.all([
        api.get('/api/admin/catalog/size-variants'),
        api.get('/api/admin/catalog/compositions'),
        api.get('/api/admin/catalog/badges'),
        api.get('/api/admin/catalog/care-instructions'),
        api.get('/api/admin/fashion-models'),
      ]);

      setSizes(sizesData);
      setCompositions(compositionsData);
      setBadges(badgesData);
      setCareInstructions(careData);
      setFashionModels(modelsData);
    } catch (error) {
      toast.error('Failed to load catalog settings');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-serif mb-2">Catalog Settings</h1>
        <p className="text-muted-foreground">
          Manage size variants, compositions, badges, care instructions and fashion models
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-6 overflow-x-auto">
          <button
            onClick={() => changeTab('sizes')}
            className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'sizes'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Size Variants ({sizes.length})
          </button>
          <button
            onClick={() => changeTab('compositions')}
            className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'compositions'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Compositions ({compositions.length})
          </button>
          <button
            onClick={() => changeTab('badges')}
            className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'badges'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Badges ({badges.length})
          </button>
          <button
            onClick={() => changeTab('care')}
            className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'care'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Care Instructions ({careInstructions.length})
          </button>
          <button
            onClick={() => changeTab('models')}
            className={`flex-shrink-0 whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition ${
              activeTab === 'models'
                ? 'border-foreground text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Models ({fashionModels.length})
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {activeTab === 'sizes' && <SizeVariantsTab sizes={sizes} onUpdate={loadData} toast={toast} />}
          {activeTab === 'compositions' && (
            <CompositionsTab
              compositions={compositions}
              careInstructions={careInstructions}
              onUpdate={loadData}
              toast={toast}
            />
          )}
          {activeTab === 'badges' && <BadgesTab badges={badges} onUpdate={loadData} toast={toast} />}
          {activeTab === 'care' && (
            <CareInstructionsTab careInstructions={careInstructions} onUpdate={loadData} toast={toast} />
          )}
          {activeTab === 'models' && (
            <FashionModelsTab models={fashionModels} sizes={sizes} onUpdate={loadData} toast={toast} />
          )}
        </>
      )}
    </div>
  );
};

// Size Variants Tab Component
const SizeVariantsTab = ({ sizes, onUpdate, toast }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSize, setEditingSize] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  // Touched-since-open flag, reset by resetForm (both on Cancel and after a
  // successful submit). handleEdit/resetForm populate via the raw setter so
  // loading a record for editing never itself counts as an edit.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  const [formData, setFormDataRaw] = useState({
    primary_name: '',
    regional_names: {},
    chest_min: '',
    chest_max: '',
    waist_min: '',
    waist_max: '',
    hips_min: '',
    hips_max: '',
    length_min: '',
    length_max: '',
    sleeve_min: '',
    sleeve_max: '',
    shoulder_min: '',
    shoulder_max: '',
    description: '',
    is_active: true,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);
  const [newRegionalName, setNewRegionalName] = useState({ country: '', name: '' });

  const COMMON_COUNTRIES = [
    { code: 'IN', name: 'India' },
    { code: 'UK', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'EU', name: 'Europe' },
    { code: 'AU', name: 'Australia' },
    { code: 'CN', name: 'China' },
  ];

  const resetForm = () => {
    setFormDataRaw({
      primary_name: '',
      regional_names: {},
      chest_min: '',
      chest_max: '',
      waist_min: '',
      waist_max: '',
      hips_min: '',
      hips_max: '',
      length_min: '',
      length_max: '',
      sleeve_min: '',
      sleeve_max: '',
      shoulder_min: '',
      shoulder_max: '',
      description: '',
      is_active: true,
    });
    setEditingSize(null);
    setShowAddForm(false);
    setIsDirty(false);
    setDuplicateWarnings([]);
    setNewRegionalName({ country: '', name: '' });
  };

  // Cancel button dismisses the form — confirm first if there's anything to
  // lose. A successful submit calls resetForm() directly, bypassing this.
  const handleCancelClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    resetForm();
  };

  const handleEdit = (size) => {
    setFormDataRaw({
      primary_name: size.primary_name || size.size_name,
      regional_names: size.regional_names || {},
      chest_min: size.chest_min ?? '',
      chest_max: size.chest_max ?? '',
      waist_min: size.waist_min ?? '',
      waist_max: size.waist_max ?? '',
      hips_min: size.hips_min ?? '',
      hips_max: size.hips_max ?? '',
      length_min: size.length_min ?? '',
      length_max: size.length_max ?? '',
      sleeve_min: size.sleeve_min ?? '',
      sleeve_max: size.sleeve_max ?? '',
      shoulder_min: size.shoulder_min ?? '',
      shoulder_max: size.shoulder_max ?? '',
      description: size.description || '',
      is_active: size.is_active,
    });
    setEditingSize(size);
    setShowAddForm(true);
  };

  const checkDuplicate = async () => {
    if (!formData.primary_name || editingSize) return; // Skip check when editing

    setCheckingDuplicate(true);
    try {
      const result = await api.post('/api/admin/catalog/size-variants/check-duplicate', formData);
      setDuplicateWarnings(result.warnings || []);
    } catch (error) {
      console.error('Duplicate check failed:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  };

  const addRegionalName = () => {
    if (newRegionalName.country && newRegionalName.name) {
      setFormData({
        ...formData,
        regional_names: {
          ...formData.regional_names,
          [newRegionalName.country]: newRegionalName.name,
        },
      });
      setNewRegionalName({ country: '', name: '' });
    }
  };

  const removeRegionalName = (country) => {
    const updated = { ...formData.regional_names };
    delete updated[country];
    setFormData({ ...formData, regional_names: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Convert empty strings to null for numeric fields
    const numericFields = [
      'chest_min',
      'chest_max',
      'waist_min',
      'waist_max',
      'hips_min',
      'hips_max',
      'length_min',
      'length_max',
      'sleeve_min',
      'sleeve_max',
      'shoulder_min',
      'shoulder_max',
    ];
    const payload = { ...formData };
    for (const field of numericFields) {
      payload[field] = payload[field] === '' || payload[field] === null ? null : parseFloat(payload[field]);
    }

    try {
      const url = editingSize
        ? `/api/admin/catalog/size-variants/${editingSize.id}`
        : '/api/admin/catalog/size-variants';

      if (editingSize) {
        await api.patch(url, payload);
      } else {
        await api.post(url, payload);
      }

      toast.success(editingSize ? 'Size updated successfully' : 'Size created successfully');
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Error saving size variant');
    }
  };

  const handleDelete = async (size) => {
    setOpenActionMenu(null);
    if (!window.confirm(`Are you sure you want to delete "${size.primary_name || size.size_name}"?`)) return;
    try {
      await api.del(`/api/admin/catalog/sizes/${size.id}`);
      toast.success('Size variant deleted');
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to delete. It may be in use by products.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-medium">Size Variants</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} />
            Add Size
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-muted/50 border border-border rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-medium mb-4">{editingSize ? 'Edit Size Variant' : 'Add New Size Variant'}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Primary Name *</label>
                <input
                  type="text"
                  value={formData.primary_name}
                  onChange={(e) => setFormData({ ...formData, primary_name: e.target.value })}
                  onBlur={checkDuplicate}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., S, M, L, XL"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">Default/fallback size name</p>
              </div>
              {[
                { label: 'Chest', key: 'chest' },
                { label: 'Waist', key: 'waist' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1">{label} (inches)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={formData[`${key}_min`]}
                      onChange={(e) => setFormData({ ...formData, [`${key}_min`]: e.target.value })}
                      onBlur={checkDuplicate}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                      placeholder="Min"
                    />
                    <span className="text-muted-foreground/70">—</span>
                    <input
                      type="number"
                      step="0.1"
                      value={formData[`${key}_max`]}
                      onChange={(e) => setFormData({ ...formData, [`${key}_max`]: e.target.value })}
                      onBlur={checkDuplicate}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                      placeholder="Max"
                    />
                  </div>
                </div>
              ))}
              {[
                { label: 'Hips', key: 'hips' },
                { label: 'Length', key: 'length' },
                { label: 'Sleeve', key: 'sleeve' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1">{label} (inches)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={formData[`${key}_min`]}
                      onChange={(e) => setFormData({ ...formData, [`${key}_min`]: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                      placeholder="Min"
                    />
                    <span className="text-muted-foreground/70">—</span>
                    <input
                      type="number"
                      step="0.1"
                      value={formData[`${key}_max`]}
                      onChange={(e) => setFormData({ ...formData, [`${key}_max`]: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                      placeholder="Max"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Shoulder (inches)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.1"
                    value={formData.shoulder_min}
                    onChange={(e) => setFormData({ ...formData, shoulder_min: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                    placeholder="Min"
                  />
                  <span className="text-muted-foreground/70">—</span>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.shoulder_max}
                    onChange={(e) => setFormData({ ...formData, shoulder_max: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                    placeholder="Max"
                  />
                </div>
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm font-medium">
                  Active
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                rows={2}
                placeholder="Optional notes about this size"
              />
            </div>

            {/* Regional Names Section */}
            <div className="mb-4 border-t border-input pt-4">
              <h4 className="text-sm font-medium mb-3">Regional Names (Size Conversions)</h4>

              {/* Existing Regional Names */}
              {Object.keys(formData.regional_names).length > 0 && (
                <div className="mb-3 space-y-2">
                  {Object.entries(formData.regional_names).map(([country, name]) => (
                    <div
                      key={country}
                      className="flex items-center gap-3 bg-card px-3 py-2 border border-border rounded-lg"
                    >
                      <span className="font-medium text-sm">{country}:</span>
                      <span className="text-sm">{name}</span>
                      <button
                        type="button"
                        onClick={() => removeRegionalName(country)}
                        className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Regional Name */}
              <div className="flex gap-2">
                <select
                  value={newRegionalName.country}
                  onChange={(e) => setNewRegionalName({ ...newRegionalName, country: e.target.value })}
                  className="px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                >
                  <option value="">Select Country</option>
                  {COMMON_COUNTRIES.filter((c) => !formData.regional_names[c.code]).map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={newRegionalName.name}
                  onChange={(e) => setNewRegionalName({ ...newRegionalName, name: e.target.value })}
                  placeholder="Size name (e.g., 10, 38)"
                  className="flex-1 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                />
                <button
                  type="button"
                  onClick={addRegionalName}
                  disabled={!newRegionalName.country || !newRegionalName.name}
                  className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-muted-foreground/50 disabled:cursor-not-allowed"
                >
                  <Plus size={18} />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Add how this size is known in different countries (e.g., IN: M, UK: 10, US: 6)
              </p>
            </div>

            {/* Duplicate Warnings */}
            {!editingSize && duplicateWarnings.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-lg">
                <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">⚠️ Warnings</h4>
                <div className="space-y-2">
                  {duplicateWarnings.map((warning, idx) => (
                    <div key={idx} className="text-sm">
                      <p className="text-amber-700 dark:text-amber-300">{warning.message}</p>
                      {warning.suggestion && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">💡 {warning.suggestion}</p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 font-medium">
                  You can still proceed, but consider using existing size variants.
                </p>
              </div>
            )}

            {checkingDuplicate && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/50 border border-blue-200 dark:border-blue-900 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                Checking for duplicates...
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={18} />
                {editingSize ? 'Update Size' : 'Create Size'}
              </button>
              <button
                type="button"
                onClick={handleCancelClick}
                className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Size Variants Table */}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Size</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Regional Names
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Measurements</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sizes.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                  No size variants configured. Click "Add Size" to create one.
                </td>
              </tr>
            ) : (
              sizes.map((size) => {
                const fmtRange = (min, max) => {
                  if (min == null && max == null) return null;
                  if (min === max || max == null) return `${min}"`;
                  if (min == null) return `${max}"`;
                  return `${min}-${max}"`;
                };
                const measurements = [
                  (size.chest_min != null || size.chest_max != null) &&
                    `Chest: ${fmtRange(size.chest_min, size.chest_max)}`,
                  (size.waist_min != null || size.waist_max != null) &&
                    `Waist: ${fmtRange(size.waist_min, size.waist_max)}`,
                  (size.hips_min != null || size.hips_max != null) && `Hips: ${fmtRange(size.hips_min, size.hips_max)}`,
                  (size.length_min != null || size.length_max != null) &&
                    `Length: ${fmtRange(size.length_min, size.length_max)}`,
                ]
                  .filter(Boolean)
                  .join(' • ');

                const regionalNames = size.regional_names
                  ? Object.entries(size.regional_names)
                      .map(([code, name]) => `${code}: ${name}`)
                      .join(', ')
                  : '-';

                return (
                  <tr key={size.id} className="hover:bg-muted/50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-foreground">{size.primary_name || size.size_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{regionalNames}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-muted-foreground">{measurements || 'No measurements'}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded-md text-xs ${
                          size.is_active
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                            : 'bg-muted text-foreground'
                        }`}
                      >
                        {size.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setOpenActionMenu(openActionMenu === size.id ? null : size.id)}
                          className="p-1 hover:bg-accent rounded-lg"
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {openActionMenu === size.id && (
                          <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                handleEdit(size);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                            >
                              <Edit2 size={14} /> Edit
                            </button>
                            <button
                              onClick={() => handleDelete(size)}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                            >
                              <Trash2 size={14} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Compositions Tab Component
const CompositionsTab = ({ compositions, careInstructions = [], onUpdate, toast }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingComposition, setEditingComposition] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  // Touched-since-open flag, reset by resetForm (both on Cancel and after a
  // successful submit). handleEdit/resetForm populate via the raw setter so
  // loading a record for editing never itself counts as an edit.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  const [formData, setFormDataRaw] = useState({
    name: '',
    alias: '',
    shell_type: '',
    description: '',
    is_active: true,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);
  const [selectedCareIds, setSelectedCareIds] = useState([]);

  const resetForm = () => {
    setFormDataRaw({ name: '', alias: '', shell_type: '', description: '', is_active: true });
    setSelectedCareIds([]);
    setEditingComposition(null);
    setShowAddForm(false);
    setIsDirty(false);
  };

  // Cancel button dismisses the form — confirm first if there's anything to
  // lose. A successful submit calls resetForm() directly, bypassing this.
  const handleCancelClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    resetForm();
  };

  const handleEdit = (composition) => {
    setFormDataRaw({
      name: composition.name,
      alias: composition.alias,
      shell_type: composition.shell_type || '',
      description: composition.description || '',
      is_active: composition.is_active,
    });
    setSelectedCareIds((composition.care_instructions_catalog || []).map((ci) => ci.id));
    setEditingComposition(composition);
    setShowAddForm(true);
  };

  const toggleCareInstruction = (id) => {
    setIsDirty(true);
    setSelectedCareIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formData, care_instruction_ids: selectedCareIds };
      const url = editingComposition
        ? `/api/admin/catalog/compositions/${editingComposition.id}`
        : '/api/admin/catalog/compositions';

      if (editingComposition) {
        await api.patch(url, payload);
      } else {
        await api.post(url, payload);
      }

      toast.success(editingComposition ? 'Composition updated' : 'Composition created');
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Error saving composition');
    }
  };

  const handleDelete = async (comp) => {
    setOpenActionMenu(null);
    if (!window.confirm(`Are you sure you want to delete "${comp.alias}"?`)) return;
    try {
      await api.del(`/api/admin/catalog/compositions/${comp.id}`);
      toast.success('Composition deleted');
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to delete. It may be in use by products.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-medium">Compositions</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} />
            Add Composition
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-muted/50 border border-border rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-medium mb-4">
              {editingComposition ? 'Edit Composition' : 'Add New Composition'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., Pure Linen"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Alias (Display Name) *</label>
                <input
                  type="text"
                  value={formData.alias}
                  onChange={(e) => setFormData({ ...formData, alias: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., 100% Linen"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Shell Type</label>
                <select
                  value={formData.shell_type}
                  onChange={(e) => setFormData({ ...formData, shell_type: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                >
                  <option value="">Any</option>
                  <option value="shell">Shell</option>
                  <option value="lining">Lining</option>
                </select>
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="comp_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="comp_active" className="text-sm font-medium">
                  Active
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                placeholder="Optional description"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Care Instructions</label>
              {careInstructions.filter((ci) => ci.is_active).length === 0 ? (
                <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg px-3 py-2">
                  No care instructions defined yet. Go to the "Care Instructions" tab to add them first.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 border border-border rounded-lg p-3 bg-card max-h-48 overflow-y-auto">
                  {careInstructions
                    .filter((ci) => ci.is_active)
                    .map((ci) => (
                      <label
                        key={ci.id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/50 p-1.5 rounded-lg"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCareIds.includes(ci.id)}
                          onChange={() => toggleCareInstruction(ci.id)}
                        />
                        {ci.icon_class && (
                          <span className="text-base" title={getLabelForClass(ci.icon_class)}>
                            {getSymbolForClass(ci.icon_class)}
                          </span>
                        )}
                        <span>{ci.instruction_text}</span>
                      </label>
                    ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Selected instructions auto-populate when this composition is chosen for a product
              </p>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={18} />
                {editingComposition ? 'Update Composition' : 'Create Composition'}
              </button>
              <button
                type="button"
                onClick={handleCancelClick}
                className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Compositions Table */}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Alias</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Care Instructions
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {compositions.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                  No compositions configured. Click "Add Composition" to create one.
                </td>
              </tr>
            ) : (
              compositions.map((comp) => (
                <tr key={comp.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{comp.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-muted-foreground">{comp.alias}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-muted-foreground capitalize">{comp.shell_type || 'Any'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {(comp.care_instructions_catalog || []).length > 0 ? (
                        comp.care_instructions_catalog.map((ci) => (
                          <span
                            key={ci.id}
                            className="text-xs bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                          >
                            {ci.icon_class && <span>{getSymbolForClass(ci.icon_class)}</span>}
                            {ci.instruction_text}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground/70">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded-md text-xs ${
                        comp.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {comp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === comp.id ? null : comp.id)}
                        className="p-1 hover:bg-accent rounded-lg"
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {openActionMenu === comp.id && (
                        <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => {
                              handleEdit(comp);
                              setOpenActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(comp)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Badges Tab Component
const BadgesTab = ({ badges, onUpdate, toast }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBadge, setEditingBadge] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  // Touched-since-open flag, reset by resetForm (both on Cancel and after a
  // successful submit). handleEdit/resetForm populate via the raw setter so
  // loading a record for editing never itself counts as an edit.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  const [formData, setFormDataRaw] = useState({
    name: '',
    display_text: '',
    color: '#000000',
    icon: '',
    applied_on: '',
    valid_till: '',
    is_active: true,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  const resetForm = () => {
    setFormDataRaw({
      name: '',
      display_text: '',
      color: '#000000',
      icon: '',
      applied_on: '',
      valid_till: '',
      is_active: true,
    });
    setEditingBadge(null);
    setShowAddForm(false);
    setIsDirty(false);
  };

  // Cancel button dismisses the form — confirm first if there's anything to
  // lose. A successful submit calls resetForm() directly, bypassing this.
  const handleCancelClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    resetForm();
  };

  const handleEdit = (badge) => {
    setFormDataRaw({
      name: badge.name,
      display_text: badge.display_text,
      color: badge.color || '#000000',
      icon: badge.icon || '',
      applied_on: badge.applied_on || '',
      valid_till: badge.valid_till || '',
      is_active: badge.is_active,
    });
    setEditingBadge(badge);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingBadge ? `/api/admin/catalog/badges/${editingBadge.id}` : '/api/admin/catalog/badges';

      if (editingBadge) {
        await api.patch(url, formData);
      } else {
        await api.post(url, formData);
      }

      toast.success(editingBadge ? 'Badge updated successfully' : 'Badge created successfully');
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Error saving badge');
    }
  };

  const handleDelete = async (badge) => {
    setOpenActionMenu(null);
    if (!window.confirm(`Are you sure you want to delete "${badge.name}"?`)) return;
    try {
      await api.del(`/api/admin/catalog/badges/${badge.id}`);
      toast.success('Badge deleted');
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to delete. It may be in use by products.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-medium">Product Badges</h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} />
            Add Badge
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-muted/50 border border-border rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-medium mb-4">{editingBadge ? 'Edit Badge' : 'Add New Badge'}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name (Internal) *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., NEW, SALE, LIMITED"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display Text *</label>
                <input
                  type="text"
                  value={formData.display_text}
                  onChange={(e) => setFormData({ ...formData, display_text: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., New Arrival, On Sale"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-16 h-10 border border-input rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="flex-1 px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                    placeholder="#000000"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Icon (Emoji/Text)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="✨ 🔥 ⭐"
                />
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="badge_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <label htmlFor="badge_active" className="text-sm font-medium">
                  Active
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Applied On (Start Date)</label>
                <input
                  type="date"
                  value={formData.applied_on}
                  onChange={(e) => setFormData({ ...formData, applied_on: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Valid Till (End Date)</label>
                <input
                  type="date"
                  value={formData.valid_till}
                  onChange={(e) => setFormData({ ...formData, valid_till: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={18} />
                {editingBadge ? 'Update Badge' : 'Create Badge'}
              </button>
              <button
                type="button"
                onClick={handleCancelClick}
                className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Badges Table */}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Badge</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Display</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Validity</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {badges.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                  No badges configured. Click "Add Badge" to create one.
                </td>
              </tr>
            ) : (
              badges.map((badge) => (
                <tr key={badge.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {badge.icon && <span className="text-lg">{badge.icon}</span>}
                      <span className="font-medium">{badge.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className="inline-block px-2 py-1 rounded-md text-white text-xs"
                      style={{ backgroundColor: badge.color }}
                    >
                      {badge.display_text}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {badge.applied_on || badge.valid_till ? (
                      <>
                        {badge.applied_on && <div>From: {badge.applied_on}</div>}
                        {badge.valid_till && <div>Until: {badge.valid_till}</div>}
                      </>
                    ) : (
                      'Always'
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded-md text-xs ${
                        badge.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {badge.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === badge.id ? null : badge.id)}
                        className="p-1 hover:bg-accent rounded-lg"
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {openActionMenu === badge.id && (
                        <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => {
                              handleEdit(badge);
                              setOpenActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(badge)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ========== Care Symbol Map ========== */
const CARE_SYMBOLS = [
  { value: 'hand_wash', label: 'Hand Wash', symbol: '\uD83E\uDDFC' },
  { value: 'machine_wash', label: 'Machine Wash', symbol: '\uD83C\uDF00' },
  { value: 'no_bleach', label: 'No Bleach', symbol: '\u26A0\uFE0F' },
  { value: 'no_tumble_dry', label: 'No Tumble Dry', symbol: '\u2B55' },
  { value: 'line_dry', label: 'Line Dry', symbol: '\uD83C\uDF2C\uFE0F' },
  { value: 'flat_dry', label: 'Flat Dry', symbol: '\u2796' },
  { value: 'iron_low', label: 'Iron Low', symbol: '\u2668\uFE0F' },
  { value: 'iron_medium', label: 'Iron Medium', symbol: '\u2668\uFE0F' },
  { value: 'no_iron', label: 'No Iron', symbol: '\uD83D\uDEAB' },
  { value: 'dry_clean', label: 'Dry Clean', symbol: '\u24B8' },
  { value: 'no_dry_clean', label: 'No Dry Clean', symbol: '\uD83D\uDEAB' },
  { value: 'no_wring', label: 'No Wring', symbol: '\u274C' },
  { value: 'wash_separate', label: 'Wash Separately', symbol: '\uD83E\uDDFA' },
  { value: 'mild_detergent', label: 'Mild Detergent', symbol: '\uD83E\uDDF4' },
  { value: 'steam_iron', label: 'Steam Iron', symbol: '\u2601\uFE0F' },
];

const getSymbolForClass = (iconClass) => {
  const match = CARE_SYMBOLS.find((s) => s.value === iconClass);
  return match ? match.symbol : '';
};

const getLabelForClass = (iconClass) => {
  const match = CARE_SYMBOLS.find((s) => s.value === iconClass);
  return match ? match.label : iconClass || '';
};

/* ========== Care Instructions Tab ========== */
const CareInstructionsTab = ({ careInstructions, onUpdate, toast }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  // Touched-since-open flag, reset by resetForm (both on Cancel and after a
  // successful submit). handleEdit/resetForm populate via the raw setter so
  // loading a record for editing never itself counts as an edit.
  const [isDirty, setIsDirty] = useState(false);
  useRegisterUnsavedChanges(isDirty);
  const [formData, setFormDataRaw] = useState({
    instruction_text: '',
    icon_class: '',
    is_active: true,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  const resetForm = () => {
    setFormDataRaw({ instruction_text: '', icon_class: '', is_active: true });
    setEditingItem(null);
    setShowAddForm(false);
    setIsDirty(false);
  };

  // Cancel button dismisses the form — confirm first if there's anything to
  // lose. A successful submit calls resetForm() directly, bypassing this.
  const handleCancelClick = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    resetForm();
  };

  const handleEdit = (item) => {
    setFormDataRaw({
      instruction_text: item.instruction_text,
      icon_class: item.icon_class || '',
      is_active: item.is_active,
    });
    setEditingItem(item);
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.patch(`/api/admin/catalog/care-instructions/${editingItem.id}`, formData);
      } else {
        await api.post('/api/admin/catalog/care-instructions', formData);
      }
      toast.success(editingItem ? 'Care instruction updated' : 'Care instruction created');
      resetForm();
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Error saving care instruction');
    }
  };

  const handleDeleteItem = async (item) => {
    setOpenActionMenu(null);
    if (!window.confirm(`Are you sure you want to delete "${item.instruction_text}"?`)) return;
    try {
      await api.del(`/api/admin/catalog/care-instructions/${item.id}`);
      toast.success('Care instruction deleted');
      onUpdate();
    } catch (error) {
      toast.error(error.message || 'Failed to delete. It may be in use by compositions.');
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-xl font-medium">Care Instructions</h2>
        {!showAddForm && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus size={18} />
            Add Care Instruction
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="bg-muted/50 border border-border rounded-lg p-6 mb-6">
          <form onSubmit={handleSubmit}>
            <h3 className="text-lg font-medium mb-4">
              {editingItem ? 'Edit Care Instruction' : 'Add New Care Instruction'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Instruction Text *</label>
                <input
                  type="text"
                  value={formData.instruction_text}
                  onChange={(e) => setFormData({ ...formData, instruction_text: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                  placeholder="e.g., Dry clean only"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Care Symbol</label>
                <select
                  value={formData.icon_class}
                  onChange={(e) => setFormData({ ...formData, icon_class: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:border-foreground"
                >
                  <option value="">No symbol</option>
                  {CARE_SYMBOLS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.symbol} {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center gap-4 mb-4">
              <input
                type="checkbox"
                id="ci_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="mr-1"
              />
              <label htmlFor="ci_active" className="text-sm font-medium">
                Active
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Save size={18} />
                {editingItem ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={handleCancelClick}
                className="px-6 py-2 border rounded-lg border-input hover:bg-muted/50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase w-16">Symbol</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Instruction</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {careInstructions.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                  No care instructions defined. Add common instructions like "Dry clean only", "Hand wash cold", etc.
                </td>
              </tr>
            ) : (
              careInstructions.map((ci) => (
                <tr key={ci.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4 text-center text-xl">
                    {ci.icon_class ? (
                      <span title={getLabelForClass(ci.icon_class)}>{getSymbolForClass(ci.icon_class)}</span>
                    ) : (
                      <span className="text-muted-foreground/50 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 font-medium text-foreground">{ci.instruction_text}</td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`inline-block px-2 py-1 rounded-md text-xs ${
                        ci.is_active
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                          : 'bg-muted text-foreground'
                      }`}
                    >
                      {ci.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenActionMenu(openActionMenu === ci.id ? null : ci.id)}
                        className="p-1 hover:bg-accent rounded-lg"
                      >
                        <MoreHorizontal size={18} />
                      </button>
                      {openActionMenu === ci.id && (
                        <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                          <button
                            onClick={() => {
                              handleEdit(ci);
                              setOpenActionMenu(null);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                          >
                            <Edit2 size={14} /> Edit
                          </button>
                          <button
                            onClick={() => handleDeleteItem(ci)}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatalogSettingsPage;
